import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import Popover from "antd/es/popover";
import Select from "antd/es/select";
import { TooltipPlacement } from "antd/es/tooltip";
import { defineMessages, useIntl } from "react-intl";

import { EntityType, EntityTypesSupportingChildLayouts } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import FormLayout from "@mapmycustomers/shared/types/layout/FormLayout";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button, Labeled } from "@mapmycustomers/ui";

import activitiesImage from "@app/assets/layouts/variant-activities.png";
import dealsImage from "@app/assets/layouts/variant-deals.png";
import EntityBadge from "@app/component/EntityBadge";
import defaultFilterOption from "@app/component/input/utils/defaultFilterOption";
import {
  getActiveEntityType,
  getEditedLayout,
} from "@app/scene/settings/component/FormLayouts/store";
import { createChildLayout } from "@app/scene/settings/component/FormLayouts/store/actions";
import { getActivityTypes } from "@app/store/activity";
import { getFunnels, getFunnelStages } from "@app/store/deal";
import { RootState } from "@app/store/rootReducer";
import defaultSelectOptionMapper from "@app/util/mappers/defaultSelectOptionMapper";
import useActivityTypeOptions from "@app/util/ui/useActivityTypeOptions";

import styles from "./VariantPopover.module.scss";

interface Props {
  activityTypes: ActivityType[];
  children?: React.ReactNode;
  createChildLayout: typeof createChildLayout;
  entityType: EntityTypesSupportingChildLayouts;
  funnels: Funnel[];
  funnelStages: Record<Funnel["id"], Stage[]>;
  layout?: FormLayout;
  placement?: TooltipPlacement;
}

const messages = defineMessages({
  clickToSelect: {
    id: "settings.formLayouts.modal.variants.clickToSelect",
    defaultMessage: "Click to select",
    description: "Click to select placeholder",
  },
  createVariantActivities: {
    id: "settings.formLayouts.modal.variants.popover.text.activities",
    defaultMessage:
      "Adding variants by activity type* enables reps to <b>see distinct forms based on the type of sales activity</b>.{br}Activities with a type that has no custom variant will still display the primary layout.{br}<i>*Only one variant can be created per type</i>",
    description: "Create Variant popover description text - activities",
  },
  createVariantDeals: {
    id: "settings.formLayouts.modal.variants.popover.text.deals",
    defaultMessage:
      "Adding variants by funnel and stage* enables reps to see <b>distinct forms based on the stage of the deal</b>.{br}Deals in a stage that has no custom variant will still display the main layout.{br}<i>*Only one variant can be created per stage</i>",
    description: "Create Variant popover description text - deals",
  },
  next: {
    id: "settings.formLayouts.modal.variants.next",
    defaultMessage: "Next",
    description: "Next button label",
  },
  selectFunnel: {
    id: "settings.formLayouts.modal.variants.selectFunnel",
    defaultMessage: "Select Funnel",
    description: "Select Funnel label",
  },
  selectFunnelFirst: {
    id: "settings.formLayouts.modal.variants.selectFunnelFirst",
    defaultMessage: "Select Funnel First",
    description: "Select Funnel First label",
  },
  selectStage: {
    id: "settings.formLayouts.modal.variants.selectStage",
    defaultMessage: "Select Stage",
    description: "Select Stage label",
  },
  selectType: {
    id: "settings.formLayouts.modal.variants.selectType",
    defaultMessage: "Select Type",
    description: "Select Type label",
  },
  title: {
    id: "settings.formLayouts.modal.variants.popover.title",
    defaultMessage: "Create Variant",
    description: "Create Variant popover title",
  },
});

const VariantPopover: React.FC<Props> = ({
  activityTypes,
  children,
  createChildLayout,
  entityType,
  funnels,
  funnelStages,
  layout,
  placement = "rightTop",
}) => {
  const intl = useIntl();
  const isDeal = entityType === EntityType.DEAL;

  const [visible, show, hide] = useBoolean();
  const [typeId, setTypeId] = useState<ActivityType["id"] | undefined>();
  const [funnelId, setFunnelId] = useState<Stage["id"] | undefined>();
  const [stage, setStage] = useState<Stage | undefined>();

  const existingStages = useMemo(
    () =>
      new Set(
        (layout?.childLayouts ?? [])
          .filter((childLayout) => childLayout?.stage?.id)
          .map((childLayout) => childLayout.stage?.id)
      ),
    [layout]
  );

  const funnelOptions = useMemo(() => {
    return funnels.map(defaultSelectOptionMapper).map((option) => ({
      ...option,
      disabled: !(funnelStages[option.value] ?? []).filter((stage) => !existingStages.has(stage.id))
        .length,
    }));
  }, [existingStages, funnels, funnelStages]);

  const activityTypeOptions = useActivityTypeOptions(
    activityTypes,
    typeId,
    useCallback(
      (type) =>
        !type.active ||
        !!(layout?.childLayouts ?? []).find(
          (childLayout) => childLayout?.crmActivityType?.id === type.id
        ),
      [layout]
    )
  );

  const stages = useMemo(
    () => (funnelId ? funnelStages[funnelId] ?? [] : []),
    [funnelId, funnelStages]
  );

  const handleStageChange = useCallback(
    (value) => {
      const stage = stages.find((stage) => stage.id === value);
      setStage(stage);
    },
    [setStage, stages]
  );

  const handleVisibleChange = useCallback(
    (visible: boolean) => {
      setFunnelId(undefined);
      setStage(undefined);
      setTypeId(undefined);

      if (visible) {
        show();
      } else {
        hide();
      }
    },
    [hide, setFunnelId, setStage, setTypeId, show]
  );

  const handleSubmit = useCallback(() => {
    switch (entityType) {
      case EntityType.ACTIVITY:
        if (typeId) {
          createChildLayout({ activityTypeId: typeId });
        }
        break;

      case EntityType.DEAL:
        if (stage?.funnel?.id && stage?.id) {
          createChildLayout({ stage });
        }
        break;
    }

    hide();
  }, [createChildLayout, entityType, hide, stage, typeId]);

  const submitButtonEnabled = isDeal ? !!funnelId && !!stage?.id : !!typeId;

  return (
    <Popover
      content={
        <div className={styles.container}>
          <div className={styles.title}>{intl.formatMessage(messages.title)}</div>

          <div className={styles.content}>
            <div className={styles.text}>
              {intl.formatMessage(
                isDeal ? messages.createVariantDeals : messages.createVariantActivities,
                {
                  b: (text) => <b>{text}</b>,
                  br: <div className={styles.para} />,
                  i: (text) => <em>{text}</em>,
                }
              )}
            </div>

            <div className={styles.image}>
              {isDeal ? (
                <img alt="Deals" src={dealsImage} />
              ) : (
                <img alt="Activities" src={activitiesImage} />
              )}
            </div>
          </div>

          <hr />

          <div className={styles.fields}>
            <div className={styles.list}>
              {isDeal ? (
                <>
                  <Labeled label={intl.formatMessage(messages.selectFunnel)}>
                    <Select<Funnel["id"]>
                      className={styles.select}
                      dropdownMatchSelectWidth
                      filterOption={defaultFilterOption}
                      onChange={setFunnelId}
                      options={funnelOptions}
                      placeholder={intl.formatMessage(messages.clickToSelect)}
                      showSearch
                      value={funnelId}
                    />
                  </Labeled>

                  <Labeled label={intl.formatMessage(messages.selectStage)}>
                    <Select<Stage["id"]>
                      className={styles.select}
                      disabled={!funnelId}
                      dropdownClassName={styles.popup}
                      dropdownMatchSelectWidth
                      filterOption={defaultFilterOption}
                      onChange={handleStageChange}
                      placeholder={
                        funnelId
                          ? intl.formatMessage(messages.clickToSelect)
                          : intl.formatMessage(messages.selectFunnelFirst)
                      }
                      showSearch
                      value={stage?.id}
                    >
                      {stages.map((stage) => (
                        <Select.Option
                          disabled={existingStages.has(stage.id)}
                          key={stage.id}
                          label={stage.name}
                          value={stage.id}
                        >
                          <EntityBadge entity={stage} />
                        </Select.Option>
                      ))}
                    </Select>
                  </Labeled>
                </>
              ) : (
                <Labeled label={intl.formatMessage(messages.selectType)}>
                  <Select<ActivityType["id"]>
                    className={styles.select}
                    dropdownMatchSelectWidth
                    filterOption={defaultFilterOption}
                    onChange={setTypeId}
                    options={activityTypeOptions}
                    placeholder={intl.formatMessage(messages.clickToSelect)}
                    showSearch
                    value={typeId}
                  />
                </Labeled>
              )}
            </div>

            <div className={styles.submit}>
              <Button disabled={!submitButtonEnabled} onClick={handleSubmit} type="primary">
                {intl.formatMessage(messages.next)}
              </Button>
            </div>
          </div>
        </div>
      }
      destroyTooltipOnHide
      onVisibleChange={handleVisibleChange}
      overlayClassName={styles.overlay}
      placement={placement}
      trigger={["click"]}
      visible={visible}
    >
      {children}
    </Popover>
  );
};

const mapStateToProps = (state: RootState) => ({
  activityTypes: getActivityTypes(state),
  entityType: getActiveEntityType(state) as EntityTypesSupportingChildLayouts,
  funnels: getFunnels(state),
  funnelStages: getFunnelStages(state),
  layout: getEditedLayout(state),
});

const mapDispatchToProps = {
  createChildLayout,
};

export default connect(mapStateToProps, mapDispatchToProps)(VariantPopover);
